import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { AssessmentResult, UserSession } from '../types';
import questionsData from '../data/questions.json';

export const generatePDF = async (result: AssessmentResult, session: UserSession) => {
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  let currentY = 20;

  // Title
  pdf.setFontSize(24);
  pdf.setTextColor(79, 70, 229);
  pdf.text('GIA-RM Assessment Report', pageWidth / 2, currentY, { align: 'center' });
  
  currentY += 15;
  pdf.setFontSize(12);
  pdf.setTextColor(100, 100, 100);
  pdf.text(`Generated: ${new Date().toLocaleDateString()}`, pageWidth / 2, currentY, { align: 'center' });
  
  // Overall Score Section
  currentY += 20;
  pdf.setFontSize(16);
  pdf.setTextColor(0, 0, 0);
  pdf.text('Overall Assessment', 20, currentY);
  
  currentY += 10;
  pdf.setFontSize(12);
  
  // Risk Level Box
  const riskColor = result.riskLevel === 'low' ? [16, 185, 129] : 
                    result.riskLevel === 'moderate' ? [245, 158, 11] : [239, 68, 68];
  pdf.setFillColor(riskColor[0], riskColor[1], riskColor[2]);
  pdf.rect(20, currentY, 50, 15, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.text(`Risk: ${result.riskLevel.toUpperCase()}`, 25, currentY + 10);
  
  // Score
  pdf.setFillColor(99, 102, 241);
  pdf.rect(80, currentY, 50, 15, 'F');
  pdf.text(`Score: ${result.overallScore}/100`, 85, currentY + 10);
  
  // Maturity Level
  pdf.setFillColor(139, 92, 246);
  pdf.rect(140, currentY, 50, 15, 'F');
  pdf.text(`Level: ${result.maturityLevel}/5`, 145, currentY + 10);
  
  currentY += 25;
  
  // Principle Scores
  pdf.setTextColor(0, 0, 0);
  pdf.setFontSize(16);
  pdf.text('Principle Scores', 20, currentY);
  
  currentY += 10;
  pdf.setFontSize(11);
  
  Object.entries(result.principleScores).forEach(([principle, score]) => {
    const principleData = questionsData.principles.find(p => p.name === principle);
    
    pdf.setTextColor(0, 0, 0);
    pdf.text(`${principleData?.icon || ''} ${principle}`, 20, currentY);
    
    // Progress bar
    const barWidth = 100;
    const barHeight = 5;
    const filledWidth = (score / 100) * barWidth;
    
    // Background
    pdf.setFillColor(229, 231, 235);
    pdf.rect(120, currentY - 3, barWidth, barHeight, 'F');
    
    // Fill
    const fillColor = score >= 70 ? [16, 185, 129] : score >= 50 ? [245, 158, 11] : [239, 68, 68];
    pdf.setFillColor(fillColor[0], fillColor[1], fillColor[2]);
    pdf.rect(120, currentY - 3, filledWidth, barHeight, 'F');
    
    // Score text
    pdf.setTextColor(79, 70, 229);
    pdf.text(`${score}%`, 225, currentY);
    
    currentY += 12;
    
    // Add new page if needed
    if (currentY > pageHeight - 30) {
      pdf.addPage();
      currentY = 20;
    }
  });
  
  // Recommendations
  currentY += 10;
  pdf.setFontSize(16);
  pdf.setTextColor(0, 0, 0);
  pdf.text('Recommendations', 20, currentY);
  
  currentY += 10;
  pdf.setFontSize(10);
  
  result.recommendations.forEach((rec, index) => {
    const lines = pdf.splitTextToSize(`${index + 1}. ${rec}`, pageWidth - 40);
    
    // Check if we need a new page
    if (currentY + (lines.length * 6) > pageHeight - 20) {
      pdf.addPage();
      currentY = 20;
    }
    
    pdf.setTextColor(50, 50, 50);
    lines.forEach((line: string) => {
      pdf.text(line, 20, currentY);
      currentY += 6;
    });
    currentY += 3;
  });
  
  // Footer
  const totalPages = (pdf as any).internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    pdf.setPage(i);
    pdf.setFontSize(9);
    pdf.setTextColor(150, 150, 150);
    pdf.text(
      `Page ${i} of ${totalPages} | GIA-RM Toolkit © ${new Date().getFullYear()}`,
      pageWidth / 2,
      pageHeight - 10,
      { align: 'center' }
    );
  }
  
  // Save PDF
  pdf.save(`GIA-RM_Assessment_Report_${new Date().toISOString().split('T')[0]}.pdf`);
};
